// React imports
import { useState, useMemo } from 'react';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// MUI Imports
import Box from '@mui/material/Box';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { grey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// FontAwesome Imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThoughtBubble, faFilterCircleDollar, faUserHeadset, faEnvelopeOpenText, faPenToSquare, faTrash, faPaperclip, faPaperPlane } from '@fortawesome/pro-light-svg-icons';

// Local imports
import readableId from '../../ReadableId.js';
import OpportunityRow from '../../OpportunityRow.js';
import TradelineRow from '../tradelines/TradelineRow.js';
import TimelineEditor from './Timeline.editor.js';
import { renderMentions } from '../../utils/mentions.js';

dayjs.extend(advancedFormat);

function MonadaTimeline(props) {
    const { organization, updates, opportunities, tradelines, log, reminders, onAddSuggestion, onHideSuggestion,
        onDeleteUpdate, onEditUpdateDone, onNewUpdate, onOpenOpportunity, onUpdateOpportunity, me, accounts,
        possibleUpdateTags, featureToggle, emails, onOpenTradeline, exchangeRates, draftKey, supplierEvents } = props;
    const theme = useTheme();

    const [ editUpdate, setEditUpdate ] = useState(null);
    const [ showRichText, setShowRichText ] = useState(false);

    const timeline = useMemo(() => {
        return ([
            ...(updates || []).filter(Boolean).map(u => ({ type: 'update', timestamp: u.created || u.timestamp, update: u })),
            ...(log || []).filter(Boolean).map(u => ({ type: 'log', timestamp: u.created || u.timestamp, update: u })),
            ...(opportunities || []).filter(Boolean).map(o => ({ type: 'opportunity', timestamp: o.created || o.timestamp || o.modified, opportunity: o })),
            ...(tradelines || []).filter(Boolean).map(o => ({ type: 'tradeline', timestamp: o.created || o.timestamp || o.modified, tradeline: o })),
            // Supplier-interaction events (RFQ sent, cost added) built in AccountsDetail for suppliers.
            ...(supplierEvents || []).filter(Boolean),
            /*
            ...(opportunities?.map(o => o.updates?.map(u => ({ type: 'opportunity-update', opportunity: o, timestamp: u.created || u.timestamp, update: u })) || [] ).flat() || []), 
            ...(opportunities?.map(o => o.log?.map(u => ({ type: 'opportunity-log', opportunity: o, timestamp: u.created || u.timestamp, update: u })) || []).flat() || []), 
            ...(tradelines?.map(o => o.updates?.map(u => ({ type: 'tradeline-update', tradeline: o, timestamp: u.created || u.timestamp, update: u })) || []).flat() || []), 
            ...(tradelines?.map(o => o.log?.map(u => ({ type: 'tradeline-log', tradeline: o, timestamp: u.created || u.timestamp, update: u })) || []).flat() || []), 
            */
        ]).sort((a,b) => b.timestamp - a.timestamp);
    }, [updates, opportunities, tradelines, log, supplierEvents]);

    function onEditUpdate(update) {
        setEditUpdate(update);
    }

    function _onEditUpdateDone(params) {
        onEditUpdateDone({ ...params, prevCreated: editUpdate.created });
        setEditUpdate(null);
    }

    return (
        <>
            <Box sx={{ marginTop: '36px', maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto', padding: '0px 12px', border: `1px solid ${grey[300]}`, borderRadius: '6px', background: 'white' }}>
                <TimelineEditor
                    organization={organization}
                    edit={showRichText}
                    possibleUpdateTags={possibleUpdateTags}
                    onEdit={() => setShowRichText(true)}
                    onUpdateDone={({ tags, html, tradelineId, newTradelineName, files }) => {
                        onNewUpdate({ tags, html, tradelineId, newTradelineName, files });
                    }}
                    onClose={() => setShowRichText(false)}
                    featureToggle={featureToggle}
                    opportunities={opportunities}
                    tradelines={tradelines}
                    draftKey={draftKey}
                />
            </Box>

            <Timeline
                sx={{
                    [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                    },
                }}
            >
                {timeline.map((entry, i) => {
                    if ((entry.type === 'update' || entry.type === 'opportunity-update' || entry.type === 'tradeline-update') && (entry.update?.deleted || entry.update?.pendingDelete)) return null;
                    if (entry.type === 'update' || entry.type === 'opportunity-update' || entry.type === 'tradeline-update') return (
                        <TimelineItem key={entry.update.created} id={entry.update.created}>
                            <TimelineSeparator>
                                <TimelineDot>
                                    <FontAwesomeIcon icon={faThoughtBubble} />
                                </TimelineDot>
                                {i < timeline.length - 1 && <TimelineConnector />}
                            </TimelineSeparator>
                            <TimelineContent sx={{ width: 'calc(100% - 32px)' }}>
                                <Typography variant='caption' component={Box} sx={{ color: grey[500], marginTop: '10px', display: 'flex', alignItems: 'center' }}>
                                    {dayjs(entry.update.created).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.update.created).fromNow()})
                                    {entry.type === 'opportunity-update' && (
                                        <>
                                            &nbsp;•&nbsp;
                                            <Button 
                                                variant='text' 
                                                color='secondary' 
                                                sx={{ textTransform: 'none', minWidth: 0, padding: '0px', fontSize: '11px !important' }}
                                                onClick={() => onOpenOpportunity({ opportunity: entry.opportunity })}
                                            >
                                                Opportunity&nbsp;created {dayjs(entry.opportunity.created).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.opportunity.created).fromNow()})
                                            </Button>
                                        </>
                                    )}
                                    {entry.type === 'tradeline-update' && (
                                        <>
                                            &nbsp;•&nbsp;
                                            <Button 
                                                variant='text' 
                                                color='primary' 
                                                sx={{ textTransform: 'none', minWidth: 0, padding: '0px', fontSize: '11px !important' }}
                                                onClick={() => onOpenTradeline({ tradeline: entry.tradeline })}
                                            >
                                                Tradeline&nbsp;<b>{entry.tradeline.title}</b>&nbsp;created {dayjs(entry.tradeline.created || entry.tradeline.modified).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.tradeline.created || entry.tradeline.modified).fromNow()})
                                            </Button>
                                        </>
                                    )}
                                </Typography>
                                <Comment
                                    comment={entry.update}
                                    showContextMenu={entry.type === 'update'}
                                    reminders={reminders}
                                    onAddSuggestion={onAddSuggestion}
                                    onHideSuggestion={onHideSuggestion}
                                    onDeleteUpdate={() => onDeleteUpdate(entry.update)}
                                    onEditUpdate={() => onEditUpdate(entry.update)}
                                    me={me}
                                    editing={editUpdate?.created === entry.update.created}
                                    onEditDone={(params) => _onEditUpdateDone(params)}
                                    onEditCancel={() => setEditUpdate(null)}
                                    organization={organization}
                                    possibleUpdateTags={possibleUpdateTags}
                                    featureToggle={featureToggle}
                                />
                            </TimelineContent>
                        </TimelineItem>
                    ); else if (entry.type === 'log' || entry.type === 'opportunity-log' || entry.type === 'tradeline-log') {
                        if (entry.update.type === 'pipeline') return (
                            <TimelineItem key={entry.update.created} id={entry.update.created}>
                                <TimelineSeparator>
                                    <TimelineDot>
                                        <FontAwesomeIcon icon={faFilterCircleDollar} />
                                    </TimelineDot>
                                    {i < timeline.length - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent>
                                    <Typography variant='caption' component={Box} sx={{ color: grey[500], marginTop: '10px' }}>
                                        {dayjs(entry.update.timestamp).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.update.timestamp).fromNow()})
                                        {entry.type === 'opportunity-log' && (
                                            <>
                                                &nbsp;•&nbsp;
                                                <Button
                                                    variant='text'
                                                    color='secondary'
                                                    sx={{ textTransform: 'none', minWidth: 0, padding: '0px', fontSize: '11px !important' }}
                                                    onClick={() => onOpenOpportunity({ opportunity: entry.opportunity })}
                                                >
                                                    Opportunity&nbsp;created {dayjs(entry.opportunity.created).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.opportunity.created).fromNow()})
                                                </Button>
                                            </>
                                        )}
                                        {entry.type === 'tradeline-log' && (
                                            <>
                                                &nbsp;•&nbsp;
                                                <Button
                                                    variant='text'
                                                    color='primary'
                                                    sx={{ textTransform: 'none', minWidth: 0, padding: '0px', fontSize: '11px !important' }}
                                                    onClick={() => onOpenTradeline({ tradeline: entry.tradeline })}
                                                >
                                                    Tradeline&nbsp;<b>{entry.tradeline.title}</b>&nbsp;created {dayjs(entry.tradeline.created || entry.tradeline.modified).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.tradeline.created || entry.tradeline.modified).fromNow()})
                                                </Button>
                                            </>
                                        )}
                                    </Typography>
                                    <Box sx={{ maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto', paddingTop: '12px' }}>
                                        <Typography variant='body2' component={Box}>
                                            Pipeline changed from <b>{entry.update.prev?.substr(10)}</b> to <b>{entry.update.new?.substr(10)}</b>.
                                        </Typography>
                                    </Box>
                                    {entry.type === 'opportunity-log' && (
                                        <Box sx={{ padding: '6px 24px', display: 'flex', fontWeight: 500 }}>
                                            OPPORTUNITY!!!
                                        </Box>
                                    )}
                                </TimelineContent>
                            </TimelineItem>
                        ); else if (entry.update.type === 'owner') return (
                            <TimelineItem key={entry.update.created} id={entry.update.created}>
                                <TimelineSeparator>
                                    <TimelineDot>
                                        <FontAwesomeIcon icon={faUserHeadset} />
                                    </TimelineDot>
                                    {i < timeline.length - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent>
                                    <Typography variant='caption' component={Box} sx={{ color: grey[500], marginTop: '10px' }}>
                                        {dayjs(entry.update.timestamp).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.update.timestamp).fromNow()})
                                        {entry.type === 'opportunity-log' && (
                                            <>
                                                &nbsp;•&nbsp;
                                                <Button
                                                    variant='text'
                                                    color='secondary'
                                                    sx={{ textTransform: 'none', minWidth: 0, padding: '0px', fontSize: '11px !important' }}
                                                    onClick={() => onOpenOpportunity({ opportunity: entry.opportunity })}
                                                >
                                                    Opportunity&nbsp;created {dayjs(entry.opportunity.created).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.opportunity.created).fromNow()})
                                                </Button>
                                            </>
                                        )}
                                        {entry.type === 'tradeline-log' && (
                                            <>
                                                &nbsp;•&nbsp;
                                                <Button
                                                    variant='text'
                                                    color='primary'
                                                    sx={{ textTransform: 'none', minWidth: 0, padding: '0px', fontSize: '11px !important' }}
                                                    onClick={() => onOpenTradeline({ tradeline: entry.tradeline })}
                                                >
                                                    Tradeline&nbsp;<b>{entry.tradeline.title}</b>&nbsp;created {dayjs(entry.tradeline.created || entry.tradeline.modified).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.tradeline.created || entry.tradeline.modified).fromNow()})
                                                </Button>
                                            </>
                                        )}
                                    </Typography>
                                    <Box sx={{ maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto', paddingTop: '12px' }}>
                                        <Typography variant='body2' component={Box}>
                                            Owner changed from <b>{entry.update.prev}</b> to <b>{entry.update.new}</b>.
                                        </Typography>
                                    </Box>
                                </TimelineContent>
                            </TimelineItem>
                        );
                    } else if (entry.type === 'opportunity') {
                        return (
                            <TimelineItem key={entry.opportunity.created} id={entry.opportunity.created}>
                                <TimelineSeparator>
                                    <TimelineDot>
                                        <FontAwesomeIcon icon={faEnvelopeOpenText} />
                                    </TimelineDot>
                                    {i < timeline.length - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ maxWidth: '100%' }}>
                                    <Typography variant='caption' component={Box} sx={{ color: grey[500], marginTop: '10px' }}>
                                        {dayjs(entry.opportunity.created).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.opportunity.created).fromNow()})
                                    </Typography>
                                    <Box sx={{ maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto' }}>
                                        <OpportunityRow
                                            opportunity={entry.opportunity}
                                            accounts={accounts}
                                            onOpen={onOpenOpportunity}
                                            onUpdateOpportunity={onUpdateOpportunity}
                                            organization={organization}
                                        />
                                    </Box>
                                </TimelineContent>
                            </TimelineItem>
                        )
                    } else if (entry.type === 'tradeline') {
                        return (
                            <TimelineItem key={entry.tradeline.created || entry.tradeline.modified} id={entry.tradeline.created || entry.tradeline.modified}>
                                <TimelineSeparator>
                                    <TimelineDot>
                                        <FontAwesomeIcon icon={faEnvelopeOpenText} />
                                    </TimelineDot>
                                    {i < timeline.length - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent>
                                    <Typography variant='caption' component={Box} sx={{ color: grey[500], marginTop: '10px' }}>
                                        {dayjs(entry.tradeline.created || entry.tradeline.modified).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.tradeline.created || entry.tradeline.modified).fromNow()})
                                    </Typography>
                                    <Box sx={{ maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto' }}>
                                        <TradelineRow
                                            tradeline={entry.tradeline}
                                            tradelines={tradelines}
                                            opportunities={opportunities}
                                            accounts={accounts}
                                            onOpen={onOpenTradeline}
                                            exchangeRates={exchangeRates}
                                            onSendUpdate={(params) => onNewUpdate({ ...params, tradelineId: entry.tradeline.id })}
                                            organization={organization}
                                        />
                                    </Box>
                                </TimelineContent>
                            </TimelineItem>
                        );
                    } else if (entry.type === 'rfq-sent' || entry.type === 'cost-added') {
                        const isRfq = entry.type === 'rfq-sent';
                        const opp = entry.opportunity;
                        const acctName = accounts?.find(a => a.id === opp.account?.id)?.name || opp.account?.name;
                        const oppLabel = `#${readableId(opp)}${acctName ? ` · ${acctName}` : ''}`;
                        return (
                            <TimelineItem key={`${entry.type}-${opp.id}-${entry.timestamp}`} id={entry.timestamp}>
                                <TimelineSeparator>
                                    <TimelineDot sx={{ background: isRfq ? '#2563eb' : '#7c3aed' }}>
                                        <FontAwesomeIcon icon={isRfq ? faPaperPlane : faFilterCircleDollar} />
                                    </TimelineDot>
                                    {i < timeline.length - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ maxWidth: '100%' }}>
                                    <Typography variant='caption' component={Box} sx={{ color: grey[500], marginTop: '10px' }}>
                                        {dayjs(entry.timestamp).format('MMMM Do YYYY, h:mm A')} ({dayjs(entry.timestamp).fromNow()})
                                    </Typography>
                                    <Box sx={{ maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto' }}>
                                        <Typography variant='body2' component={Box}>
                                            {isRfq
                                                ? 'RFQ sent to this supplier'
                                                : `Cost from this supplier added${entry.count > 1 ? ` (${entry.count} charges)` : ''}`}
                                            {' in opportunity '}
                                            <Box component='span' onClick={() => onOpenOpportunity({ opportunity: opp })} sx={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                                                {oppLabel}
                                            </Box>
                                        </Typography>
                                    </Box>
                                </TimelineContent>
                            </TimelineItem>
                        );
                    }
                })}
            </Timeline>

        </>
    )
}

export default MonadaTimeline;

function Comment(props) {
    const { comment, reminders, onAddSuggestion, onHideSuggestion, onDeleteUpdate, onEditUpdate, me, showContextMenu,
        editing, onEditDone, onEditCancel, organization, possibleUpdateTags, featureToggle } = props;
    const [ confirmDelete, setConfirmDelete ] = useState(false);

    if (comment.deleted || comment.pendingDelete) {
        return null;
    }

    if (editing) {
        return (
            <Box sx={{ maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto', padding: '12px', border: `1px solid ${grey[300]}`, borderRadius: '6px', background: 'white', marginTop: '12px' }}>
                <TimelineEditor
                    organization={organization}
                    edit={true}
                    initialUpdate={comment}
                    onUpdateDone={(params) => onEditDone(params)}
                    onClose={onEditCancel}
                    possibleUpdateTags={possibleUpdateTags}
                    featureToggle={featureToggle}
                    noPadding
                />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto', padding: '16px', border: `1px solid #e2e8f0`, borderRadius: '8px', background: 'white', marginTop: '10px' }} key={comment.created}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Box sx={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#64748b' }}>
                        {(comment.createdBy || '?')[0].toUpperCase()}
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#334155', lineHeight: 1.2 }}>
                            {(comment.createdBy || '').split('@')[0]}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: '#94a3b8', lineHeight: 1.2 }}>
                            {dayjs(comment.created).fromNow()}{(comment.editHtml || comment.wasEdited) ? ' · edited' : ''}
                        </Typography>
                    </Box>
                </Box>
                {showContextMenu && (
                    <Box sx={{ display: 'flex', gap: '4px', opacity: 0.4, '&:hover': { opacity: 1 }, transition: 'opacity 0.15s' }}>
                        <IconButton size='small' onClick={() => onEditUpdate(comment)} sx={{ color: grey[500], '&:hover': { color: grey[700] } }}>
                            <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: '12px' }} />
                        </IconButton>
                        <IconButton size='small' onClick={() => setConfirmDelete(true)} sx={{ color: grey[500], '&:hover': { color: '#ef5350' } }}>
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px' }} />
                        </IconButton>
                    </Box>
                )}
            </Box>
            {confirmDelete && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1, p: 1, borderRadius: '4px', backgroundColor: '#fef2f2' }}>
                    <Typography variant='caption' sx={{ flex: 1 }}>Delete this comment?</Typography>
                    <Button size='small' variant='contained' color='error' onClick={() => onDeleteUpdate(comment)}>Delete</Button>
                    <Button size='small' variant='outlined' onClick={() => setConfirmDelete(false)}>Cancel</Button>
                </Box>
            )}
            <Box sx={{ fontSize: '13px', lineHeight: 1.6, color: '#334155', '& p': { margin: '4px 0' }, '& h1, & h2, & h3': { fontSize: '14px', margin: '6px 0 4px' }, '& ul, & ol': { margin: '4px 0', paddingLeft: '20px' }, '& a': { color: '#3b82f6' } }}
                dangerouslySetInnerHTML={{ __html: renderMentions({ html: comment.editHtml || comment.html, me: me.email, users: organization?.users }) }}
            />
            {(comment.actionsSuggestions || [])
                .filter(s => !(reminders || []).find(r => r.id === s.id))
                .map(suggestion => (
                    <Box key={suggestion.id} sx={{ display: 'flex', alignItems: 'center', mt: 1, p: 1, borderRadius: '6px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                        <Typography sx={{ flex: 1, fontSize: '12px', color: '#0369a1' }}>
                            Add reminder: <b>"{suggestion.description}"</b> due {new Date(suggestion.dueDate).toDateString()}
                        </Typography>
                        <Button size='small' variant='text' sx={{ textTransform: 'none', minWidth: 0, fontSize: '11px' }} onClick={() => onAddSuggestion({ suggestion })}>Yes</Button>
                        <Button size='small' variant='text' color='secondary' sx={{ textTransform: 'none', minWidth: 0, fontSize: '11px' }} onClick={() => onHideSuggestion({ suggestion })}>No</Button>
                    </Box>
                ))}
            {comment.attachments && comment.attachments.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {comment.attachments.map((file, index) => (
                        <Chip
                            key={index}
                            size='small'
                            label={<Box sx={{ display: 'flex', alignItems: 'center', fontSize: '11px' }}>
                                <FontAwesomeIcon icon={faPaperclip} style={{ marginRight: '4px', fontSize: '10px' }} />
                                {file.name}
                            </Box>}
                            component="a"
                            href={file.url}
                            target="_blank"
                            clickable
                            sx={{ height: '22px' }}
                        />
                    ))}
                </Box>
            )}
            {(comment.editTags || comment.tags)?.length > 0 && (
                <Box sx={{ display: 'flex', gap: '4px', mt: 1, flexWrap: 'wrap' }}>
                    {(comment.editTags || comment.tags).map(tag => (
                        <Chip key={tag.label} label={tag.label} size='small' sx={{ fontSize: '10px', height: '20px' }} />
                    ))}
                </Box>
            )}
        </Box>
    )
}
